import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TippyDirective } from '@ngneat/helipopper';
import { map, Observable } from 'rxjs';

import { isCsgoItem, Item } from '@app/modules/items';
import { ItemImagePipe } from '@app/modules/items/pipes';
import { AuthState } from '@app/modules/users';
import {
  isSteamWithdrawal,
  SteamWithdrawalContext,
  SteamWithdrawalStatus,
  Withdrawal,
  WithdrawalsState,
  WithdrawalStatus
} from '@app/modules/withdrawals';
import { MethodIconUrlPipe } from '@app/modules/withdrawals/pipes';
import { CurrencyInputComponent } from '@app/ui/components/currency-input/currency-input.component';
import { MediaQueryService } from '@core/breakpoints';
import { DateUtcComponent } from '@core/date';
import {
  addLeadingZero,
  Countdown,
  countdown,
  indicateLoading
} from '@core/helpers';
import { CryptoWithdrawalContext } from '@app/modules/withdrawals/withdrawals.entity';
import { Clipboard } from '@angular/cdk/clipboard';
import { Notifier } from '@core/notifications';

@Component({
  selector: 'withdrawals-table-item',
  templateUrl: './withdrawals-item.component.html',
  styleUrl: './withdrawals-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    DateUtcComponent,
    TippyDirective,
    AsyncPipe,
    ItemImagePipe,
    MethodIconUrlPipe,
    CurrencyInputComponent
  ]
})
export class WithdrawalsItemComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authState = inject(AuthState);
  private readonly withdrawalsState = inject(WithdrawalsState);
  private readonly mediaService = inject(MediaQueryService);
  private readonly clipboard = inject(Clipboard);
  private readonly notifier = inject(Notifier);

  @Input('withdrawal')
  set _withdrawal(withdrawal: Withdrawal) {
    this.withdrawal = withdrawal;

    if(isSteamWithdrawal(withdrawal)) {
      if(withdrawal.result && withdrawal.status !== WithdrawalStatus.DONE) {
        const { status } = withdrawal.result;

        if(status === SteamWithdrawalStatus.SENT ||
          status === SteamWithdrawalStatus.WAITING_SELLER
        ) {
          this.setupCountdown();
        }

        if(status === SteamWithdrawalStatus.SENT) {
          const { tradeId } = withdrawal.result;

          this.tradeOfferUrl = `https://steamcommunity.com/tradeoffer/${ tradeId! }`;
        }

        if(status === SteamWithdrawalStatus.WAITING_SELLER) {
          this.statusTooltip = $localize`:@@profile.waiting_seller:Ожидание продавца`;
        }
      }
      
      this.item = (withdrawal.context as SteamWithdrawalContext).item;
    } else {
      if (withdrawal.context && Object.keys(withdrawal.context).length > 0) {
        this.cryptoItem = (withdrawal.context as CryptoWithdrawalContext);
      }
    }
  }

  public readonly isCancelling = signal<boolean>(false);
  public readonly isLg = toSignal(this.mediaService.mediaQuery('max', 'lg'));
  public readonly WithdrawalStatus = WithdrawalStatus;
  public readonly isCsgoItem = isCsgoItem;
  
  public countdown$?: Observable<string>;
  public withdrawal!: Withdrawal;
  public item?: Item;
  public cryptoItem?: CryptoWithdrawalContext;
  public tradeOfferUrl?: string;
  public statusTooltip = '';

  cancelWithdrawal(): void {
    this.withdrawalsState.cancelWithdrawal(this.withdrawal.id)
      .pipe(
        indicateLoading(this.isCancelling),
        takeUntilDestroyed(this.destroyRef) 
      )
      .subscribe();
  }

  copyAddress(address: string|undefined): void {
    if (address) {
      this.clipboard.copy(address);
    }
  }

  private setupCountdown(): void {
    if(!this.withdrawal || !this.withdrawal.result) {
      return;
    }

    const timeShift = this.authState.timeDiff();

    this.countdown$ = countdown(this.withdrawal.result.timeoutAt, { timeShift })
      .pipe(
        map((cd: Countdown) => {
          const minutes = addLeadingZero(cd.duration!.minutes(), false);
          const seconds = addLeadingZero(cd.duration!.seconds(), false);

          return `${ minutes }:${ seconds }`;
        })
      );
  }
}
